import React, { useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import DictationButton from './DictationButton';
import { auth } from '../firebase';
import { adverseFor, ADVERSE_AUTHORITY_COUNT } from '../data/adverseAuthority';
import { ANALYZE_URL } from '../installation';   // ruling 312: the functions host comes from the installation
import { wordsFor } from '../data/craftTerms';   // section 807: the words of the claim's division

const CLOUD_FUNCTION_URL = ANALYZE_URL;

const NARRATIVE_QUESTIONS_BASE = [
  { id: 'date_time', label: 'Date & Time', question: 'What was the exact date and time of the incident?' },
  { id: 'location_type', label: 'Location & Manner', question: 'Where did the conversation take place and how (in person, by phone, over radio, or in writing)?' },
  { id: 'trainmaster', label: 'Trainmaster / Manager', question: 'What is the name and title of the manager who made the assignment?', filledBy: (f) => f && f.personsInvolved },
  { id: 'conversation', label: 'What Was Said', question: 'Describe as accurately as possible what was said between you and the manager.' },
  { id: 'junior_ym', labelKey: 'narrativeJuniorLabel', questionKey: 'narrativeJuniorQuestion', filledBy: (f) => f && f.violatorName },
  { id: 'seniority', label: 'Your Seniority Standing', question: 'What was your availability status and seniority standing at the time of the incident?', filledBy: (f) => f && f.rosterNumber && f.seniorityDate },
  { id: 'assignment_type', label: 'Type of Assignment', question: 'Was this a regular assignment, overtime call, emergency fill, or other type of vacancy?', filledBy: (f) => f && f.violationType },
  { id: 'witnesses', label: 'Witnesses', question: 'Were there any witnesses present? If so, who?', filledBy: (f) => f && f.witnesses },
  { id: 'outcome', label: 'Outcome', question: 'What was the financial impact to you — hours lost, rate applied, and total amount?' },
];

const LANGUAGE_FILTER = [
  /\b(damn|hell|ass|bastard|idiot|stupid|moron|dumb|jerk|fool)\b/gi,
  /\b(threat|kill|hurt|beat|attack)\b/gi,
];

// WHOSE WORDS THESE ARE (corrected 2026-09-22, section 364, D10).
//
// This function is still used on TEXT THE MODEL WROTE - cleaning the platform's own output takes
// nothing from anybody. It is NO LONGER used on what the member types. It used to run on every
// keystroke of his account of what happened, turning "threat", "hurt" and "attack" into "[removed]"
// without telling him - which is the silent alteration of a claimant's alleged facts that ruling 138
// forbids, in precisely the words he needs to describe being threatened.
function cleanLanguage(text) {
  let clean = text;
  LANGUAGE_FILTER.forEach(pattern => {
    clean = clean.replace(pattern, '[removed]');
  });
  return clean;
}

export default function ClaimNarrative({ claimId, description, matchedRules, railroad, agreement, user, onNarrativeSaved, formData }) {
  // Section 807: questions worded for the claim's own division.
  const NARRATIVE_QUESTIONS = NARRATIVE_QUESTIONS_BASE.map((q) => (q.labelKey ? { ...q, label: wordsFor((formData || {}).craft)[q.labelKey], question: wordsFor((formData || {}).craft)[q.questionKey] } : q));
  const [answers, setAnswers] = useState(() => {
    const pre = {};
    if (formData) {
      if (formData.incidentDate || formData.incidentTime12) {
        // Forensic audit 2026-10-16: new Date('YYYY-MM-DD') is read as UTC midnight, which in every US zone is the
        // evening BEFORE - the pre-filled incident date came out one day early. Read at local noon, as ClaimDetail does.
        const rawDate = String(formData.incidentDate || '');
        const d = rawDate ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate + 'T12:00:00' : rawDate).toLocaleDateString('en-US', {month:'long', day:'numeric', year:'numeric'}) : '';
        const t = formData.incidentTime12 || '';
        pre['date_time'] = [d, t].filter(Boolean).join(' at ') || '';
      }
      if (formData.yardOfIncident) pre['location_type'] = formData.yardOfIncident;
      if (formData.violatorName) {
        pre['junior_ym'] = formData.violatorName + (formData.violatorRosterNumber ? ' (Roster #' + formData.violatorRosterNumber + ')' : '');
      }
      if (formData.witnesses) pre['witnesses'] = formData.witnesses;
      if (formData.totalHoursClaimed || formData.totalAmountOT) {
        pre['outcome'] = (formData.totalHoursClaimed ? formData.totalHoursClaimed + ' hours' : '') +
          (formData.totalAmountOT ? ' — $' + formData.totalAmountOT : '');
      }
      if (formData.rosterNumber || formData.seniorityDate) {
        pre['seniority'] = (formData.rosterNumber ? 'Roster #' + formData.rosterNumber : '') +
          (formData.seniorityDate ? ', Seniority Date: ' + formData.seniorityDate : '');
      }
      if (formData.violationType || (matchedRules && matchedRules.length > 0)) {
        pre['assignment_type'] = matchedRules && matchedRules.length > 0
          ? matchedRules.map(r => r.rule).join(', ')
          : formData.violationType || '';
      }
    }
    return pre;
  });
  const [narrative, setNarrative] = useState('');
  const [generating, setGenerating] = useState(false);
  const [approved, setApproved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedNarrative, setEditedNarrative] = useState('');
  // Forensic audit 2026-10-16: "Preview" turned `editing` off, and both the preview and Approve read `editing` to pick
  // the text - so a member who edited, previewed, then approved saw and saved the ORIGINAL, his edits discarded. Once
  // he has typed in it, his edited text is the narrative everywhere (preview, review, approve, the approved record).
  const [edited, setEdited] = useState(false);
  const currentNarrative = edited ? editedNarrative : narrative;
  const [error, setError] = useState('');
  const [step, setStep] = useState('questions'); // questions | narrative | approved

  // [VERIFY] GATE (Track 4). Ruling 16 puts enforcement in the rules layer where a stale tab cannot
  // defeat it; this is the CLAIMANT-FACING half - a member must not approve a narrative into a
  // subject where the platform already holds an adverse board award and say nothing about it.
  // See src\data\adverseAuthority.js for what this table may and may not assert.
  const adverse = adverseFor({ railroad, agreement, matchedRules, violationType: formData && formData.violationType });
  const [adverseAck, setAdverseAck] = useState(false);
  const gateBlocked = adverse.length > 0 && !adverseAck;

  // The member's own answers, unfiltered. What he types is what the record holds (rulings 136, 138).
  const setAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));
  const appendAnswer = (id, t) => setAnswers(prev => ({ ...prev, [id]: (prev[id] ? prev[id].trim() + ' ' : '') + t }));

  // Filter questions already answered by form data
  // Filter out questions where the answer is already in the form data
  const visibleQuestions = NARRATIVE_QUESTIONS.filter(q => {
    if (!q.filledBy) return true;
    try {
      return !q.filledBy(formData || {});
    } catch(e) {
      return true;
    }
  });
  const answeredCount = Object.values(answers).filter(v => v?.trim()).length;
  const canGenerate = answeredCount >= Math.min(4, Math.max(1, visibleQuestions.length));

  const generateNarrative = async () => {
    setGenerating(true);
    setError('');

    const answersText = NARRATIVE_QUESTIONS
      .filter(q => answers[q.id]?.trim())
      .map(q => `${q.label}: ${answers[q.id]}`)
      .join('\n');

    const prompt = `You are a professional railroad labor relations expert writing a formal grievance claim narrative.

Claimant's Description: ${description}

Additional Facts Provided:
${answersText}

Rule / basis of claim: ${matchedRules?.map(r => r.rule).join(', ')}
Railroad: ${railroad}
Agreement: ${agreement}

Write a professional, factual claim narrative for this grievance. Requirements:
- Short, clear, and professional — no emotional language
- State the facts in chronological order
- Include: date/time, location, conversation details, parties involved, seniority standing, management's role in filling the vacancy (yardmaster working out of seniority is NOT at fault — management fills vacancies)
- Close with the specific contractual violation and remedy requested
- Maximum 200 words
- No profanity, threats, insults, or abusive language
- First person voice from the claimant

Return ONLY the narrative text. No preamble. No labels.`;

    try {
      const res = await fetch(CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (await auth.currentUser?.getIdToken() || '') },
        body: JSON.stringify({
          mode: 'narrative',
          agreement,
          description: prompt,
          availableRules: []
        })
      });
      const data = await res.json();
      const text = data.narrative || data.rules?.[0]?.exactLanguage || '';
      if (text) {
        setNarrative(cleanLanguage(text));
        setEditedNarrative(cleanLanguage(text));
        setEdited(false);   // a regenerated narrative is a new text; the member asked for it
        setStep('narrative');
      } else {
        setError('Narrative generation failed. Please try again.');
      }
    } catch (e) {
      setError('Could not generate narrative: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = () => {
    // The button is disabled while blocked; this is the second door, so a stale render or a
    // programmatic call cannot walk past the gate.
    if (adverse.length > 0 && !adverseAck) return;
    setApproved(true);
    setStep('approved');
    // The acknowledgement travels WITH the claim. A gate nobody can show was shown is not a gate.
    const out = adverse.length > 0
      ? { ...answers, _adverse_authority_ack: adverse.map(a => a.citation).join('; ') }
      : answers;
    if (onNarrativeSaved) onNarrativeSaved(currentNarrative, out);   // forensic audit 2026-10-16: his edit, whether or not the box is open
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">Claim Narrative</div>
        <div style={{ fontSize: 11, color: '#8AA4BF' }}>
          {step === 'questions' && `${answeredCount} of ${NARRATIVE_QUESTIONS.length} questions answered`}
          {step === 'narrative' && (approved ? '✅ Approved' : 'Pending claimant approval')}
          {step === 'approved' && '✅ Narrative approved and saved'}
        </div>
      </div>

      {/* STEP 1 — Questions */}
      {step === 'questions' && (
        <div>
          <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 16 }}>
            Answer the questions below. The platform will write the formal claim narrative from your answers.
            Answer at least 4 to generate the narrative.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {NARRATIVE_QUESTIONS.map(q => (
              <div key={q.id} style={{ background: '#0A1628', border: `1px solid ${answers[q.id]?.trim() ? '#2E6B4A' : '#2E4A6B'}`, borderRadius: 6, padding: '14px 16px' }}>
                <div style={{ fontSize: 12, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontWeight: 700 }}>
                  {q.label} {answers[q.id]?.trim() && <span style={{ color: '#40C070' }}>✓</span>}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: 14, color: '#F4F6F8' }}>{q.question}</div>
                  <DictationButton compact onText={t => appendAnswer(q.id, t)} />
                </div>
                <textarea aria-label="Your answer..."
                  style={{ width: '100%', background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 4, padding: '10px 12px', color: '#F4F6F8', fontSize: 15, resize: 'vertical', minHeight: 96, boxSizing: 'border-box' }}
                  placeholder="Your answer..."
                  value={answers[q.id] || ''}
                  onChange={e => setAnswer(q.id, e.target.value)}
                />
              </div>
            ))}
          </div>

          {error && <div style={{ color: '#FF6B6B', fontSize: 12, marginTop: 8 }}>⚠ {error}</div>}

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={generateNarrative}
              disabled={!canGenerate || generating}
              style={{
                padding: '10px 20px', background: canGenerate ? '#1A3A5C' : '#0D1B2A',
                border: `1px solid ${canGenerate ? '#E8A020' : '#2E4A6B'}`,
                borderRadius: 6, color: canGenerate ? '#E8A020' : '#4A6280',
                fontSize: 13, fontWeight: 700, cursor: canGenerate ? 'pointer' : 'not-allowed',
              }}>
              {generating ? '⚙ Writing narrative...' : '✍ Generate Claim Narrative'}
            </button>
            {!canGenerate && <div style={{ fontSize: 11, color: '#4A6280' }}>Answer at least {Math.min(4, visibleQuestions.length)} questions to generate.</div>}
          </div>
        </div>
      )}

      {/* STEP 2 — Narrative review */}
      {step === 'narrative' && (
        <div>
          <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 12 }}>
            Review the claim narrative below. This was written from your answers and will become part of the official claim record.
            You may edit it before approving.
          </div>

          {!editing ? (
            <div style={{ background: '#0A1628', border: '1px solid #2E4A6B', borderRadius: 6, padding: '16px 18px', marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{currentNarrative}</div>
              <DocumentReview title="the narrative" kind="narrative" text={currentNarrative} facts={{ rules: (matchedRules || []).map(r => r && r.rule).filter(Boolean) }} record={description ? 'The claimant\u2019s account: ' + String(description).slice(0, 2000) : ''} />
            </div>
          ) : (
            <textarea aria-label="Edited narrative"
              style={{ width: '100%', background: '#0A1628', border: '1px solid #E8A020', borderRadius: 6, padding: '14px 16px', color: '#F4F6F8', fontSize: 13, lineHeight: 1.7, minHeight: 180, boxSizing: 'border-box', resize: 'vertical' }}
              value={editedNarrative}
              // His edit of the composed narrative is HIS text, and is not filtered either (D10).
              onChange={e => { setEditedNarrative(e.target.value); setEdited(true); }}
            />
          )}

          {/* [VERIFY] GATE - adverse authority on this subject, on this property */}
          {adverse.length > 0 && (
            <div style={{ background: '#2A1206', border: '1px solid #E8A020', borderRadius: 6, padding: '14px 18px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#E8A020', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
                READ BEFORE YOU APPROVE - A BOARD HAS DECIDED AGAINST THIS THEORY ON THIS PROPERTY
              </div>
              {adverse.map(a => (
                <div key={a.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #4A3520' }}>
                  <div style={{ fontSize: 13, color: '#F4F6F8', fontWeight: 700 }}>{a.citation} - {a.outcome}</div>
                  <div style={{ fontSize: 12, color: '#F4F6F8', lineHeight: 1.6, marginTop: 6 }}>
                    <b>What was in dispute:</b> {a.subjectMatter}
                  </div>
                  <div style={{ fontSize: 12, color: '#E8A020', lineHeight: 1.6, marginTop: 6 }}>
                    <b>{a.readingState}.</b> {a.caveat}
                  </div>
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>
                    {a.board}<br />Award: <code>{a.document}</code> &nbsp;|&nbsp; Dissent: <code>{a.dissent}</code><br />{a.provenance}
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 10 }}>
                This does not mean your claim fails. It means the carrier can cite this, and you should
                know that before you file rather than after.
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#F4F6F8', cursor: 'pointer' }}>
                <input aria-label="Adverse ack" type="checkbox" checked={adverseAck} onChange={e => setAdverseAck(e.target.checked)} style={{ marginTop: 3 }} />
                <span>I have read the adverse authority above and I am approving this narrative anyway.</span>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={handleApprove} disabled={gateBlocked}
              title={gateBlocked ? 'Acknowledge the adverse authority above before approving.' : undefined}
              style={{ padding: '8px 20px', background: gateBlocked ? '#1C3A28' : '#27AE60', border: 'none', borderRadius: 6, color: gateBlocked ? '#5A7A68' : 'white', fontSize: 13, fontWeight: 700, cursor: gateBlocked ? 'not-allowed' : 'pointer' }}>
              ✓ Approve Narrative
            </button>
            <button type="button" onClick={() => setEditing(!editing)}
              style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #8AA4BF', borderRadius: 6, color: '#8AA4BF', fontSize: 12, cursor: 'pointer' }}>
              {editing ? 'Preview' : '✏ Edit'}
            </button>
            <button type="button" onClick={() => setStep('questions')}
              style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #2E4A6B', borderRadius: 6, color: '#4A6280', fontSize: 12, cursor: 'pointer' }}>
              ← Revise Answers
            </button>
            <button type="button" onClick={generateNarrative} disabled={generating}
              style={{ padding: '8px 16px', background: 'transparent', border: '1px solid #2E4A6B', borderRadius: 6, color: '#4A6280', fontSize: 12, cursor: 'pointer' }}>
              🔄 Regenerate
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Approved */}
      {step === 'approved' && (
        <div>
          <div style={{ background: '#0A2A1A', border: '1px solid #27AE60', borderRadius: 6, padding: '14px 18px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#40C070', fontWeight: 700, marginBottom: 6 }}>
              ✅ Narrative approved by {user?.name || user?.email}
            </div>
            <div style={{ fontSize: 13, color: '#F4F6F8', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{currentNarrative}</div>
          </div>
          <div style={{ fontSize: 11, color: '#8AA4BF' }}>
            The Local Chair and General Chair may further refine this narrative as the case develops.
          </div>
          {/* Never let a silent pass read as "checked against the case law". It was checked against
              the linkages the platform actually holds, and that number is small. */}
          <div style={{ fontSize: 11, color: '#8AA4BF', marginTop: 6 }}>
            Adverse-authority check: {adverse.length > 0
              ? `${adverse.length} acknowledged.`
              : 'nothing established for this property and subject.'}{' '}
            The platform holds {ADVERSE_AUTHORITY_COUNT} adverse authority linkage(s) in total - a
            clean result here means nothing is on file, not that none exists.
          </div>
        </div>
      )}
    </div>
  );
}
